import { useState, useEffect } from 'react';
import { CartDetails, ProductDetails } from '../models/product';
import { getItem, setItem, CART_STORAGE_KEY } from '../utils/local-storage';

function clampQuantity(qty: number): number {
  if (qty < 0) return 0;
  if (qty > 100) return 100;
  return qty;
}

export const useCart = (productDetails: ProductDetails[]) => {
  const [cartItems, setCartItems] = useState<CartDetails>(() => {
    const stored = getItem<CartDetails>(CART_STORAGE_KEY) ?? {};
    return stored;
  });

  useEffect(() => {
    if (productDetails.length === 0) return;
    const stored = getItem<CartDetails>(CART_STORAGE_KEY) ?? {};
    setCartItems((prev) => {
      const next: CartDetails = {};
      productDetails.forEach(({ name }) => {
        const storedQty = stored[name];
        const prevQty = prev[name];
        let qty = storedQty ?? prevQty ?? 0;
        qty = clampQuantity(qty);
        next[name] = qty;
      });
      return next;
    });
  }, [productDetails]);

  useEffect(() => {
    setItem(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  const addToCart = (itemName: string, amount = 1) => {
    setCartItems((prev) => {
      const current = prev[itemName] ?? 0;
      let newAmount = current + amount;
      if (newAmount > 1000) newAmount = 1000;
      return { ...prev, [itemName]: newAmount };
    });
  };

  const removeFromCart = (itemName: string, amount = 1) => {
    setCartItems((prev) => {
      const current = prev[itemName] ?? 0;
      let newAmount = current - amount;
      if (newAmount < 0) newAmount = 0;
      return { ...prev, [itemName]: newAmount };
    });
  };

  const clearCart = () => {
    const reset: CartDetails = {};
    productDetails.forEach(({ name }) => {
      reset[name] = 0;
    });
    setCartItems(reset);
  };

  const setCartQuantity = (itemName: string, amount: number) => {
    if (amount < 0) {
      throw Error('Cannot set cart quantity to negative number');
    }
    if (!Number.isInteger(amount)) {
      throw Error('Cannot set cart quantity to non integer value');
    }
    setCartItems((prev) => ({ ...prev, [itemName]: amount }));
  };

  return { cartItems, addToCart, removeFromCart, setCartQuantity, clearCart };
};
